/**
 * D282: sessions open to remote control, as rows that open the Live page. Home's "Live now" lists the
 * person's own (`GET /people/v1/me/live`); a project's History lists what its members may watch
 * (`GET /projects/v1/:id/live`). Each row: the session's title, its kind and machine in words, whether
 * the machine is awake (a StatusMark), and who is controlling it. Nothing is drawn when there is nothing live.
 */

import type { LiveSessionRow } from '@aioe/shared';
import { controllerWords } from '@aioe/shared';
import { Pulse } from '@gravity-ui/icons';
import { useEffect, useSyncExternalStore } from 'react';

import { useAuth } from '../../auth/AuthProvider.tsx';
import { hasDirectory, resolveSubjects, subjectName, subscribeNames } from '../../directory.ts';
import { ListGroup, ListRow, StatusMark } from '../ui.tsx';
import type { ArtTone } from '../ui/Art.tsx';
import { liveHref, MODE_WORDS } from './liveModel.ts';

const firstOf = (name: string | undefined) => name?.trim().split(/\s+/)[0] || undefined;

/**
 * The first name of the person a session runs for: the signed-in person for their own, else the
 * directory's name for `person`; undefined when nobody can say (the page then names the machine).
 */
export function useSessionPerson(row: Pick<LiveSessionRow, 'canControl'> & { session: { person?: string } } | undefined): string | undefined {
  const { account } = useAuth();
  const person = row?.session.person;
  const mine = Boolean(row?.canControl || (person && person === account?.id));
  const s = useSyncExternalStore(subscribeNames, () => (person ? subjectName(person) : undefined));
  useEffect(() => {
    if (person && !mine && hasDirectory()) void resolveSubjects([person]);
  }, [person, mine]);
  if (mine) return firstOf(account?.name);
  return s && !s.missing && s.name !== person ? firstOf(s.name) : undefined;
}

/** "JP's workbench", or the machine's name when the person is not known. */
export const workbenchWords = (who: string | undefined, deviceName: string | undefined) => (who ? `${who}'s workbench` : deviceName?.trim() || 'The workbench');

/** One live session as a row: you press it to open the Live page; a row with no address (an older workbench) is read only. */
function SessionRow({ r, project }: { r: LiveSessionRow; project?: string }) {
  const href = liveHref(r.deviceId, r.session, project);
  const by = r.session.controlledBy;
  const line = [MODE_WORDS[r.session.mode], `on ${r.deviceName}`, by ? `being controlled from ${controllerWords(by.name, by.via)}` : r.canControl ? undefined : 'you can watch'].filter(Boolean).join(' · ');
  const props = { icon: <Pulse />, title: r.session.title || 'Untitled session', description: <span className="block truncate">{line}</span>, status: <StatusMark state={r.online ? 'online' : 'offline'} label={r.online ? 'Awake' : 'Asleep'} /> };
  return href ? <ListRow to={href} {...props} /> : <ListRow {...props} />;
}

/** A group of live sessions (Layout.tsx); hidden while there are none, while loading, and when the list could not be read. */
export function LiveSessionsSection({ title, hint, rows, project }: { title: string; hint?: string; rows: LiveSessionRow[] | undefined; tone?: ArtTone; project?: string }) {
  if (!rows?.length) return null;
  return (
    <ListGroup title={title} count={rows.length} description={hint}>
      {rows.map((r) => (
        <SessionRow key={`${r.deviceId}/${r.session.conversationId ?? r.session.sessionId ?? r.session.title}`} r={r} project={project} />
      ))}
    </ListGroup>
  );
}
